import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, Heart, Zap, Sparkles, Search, Users2 } from "lucide-react";
import { SPEAKER_IMG, FOUNDER_SPEAKING, FOUNDER_SEATED } from "@/lib/constants";

const archetypes = [
  { name: "Visionary", icon: Eye, body: "Sees around corners. Sets direction. Sometimes struggles with the how." },
  { name: "Empathetic", icon: Heart, body: "Reads the room. Holds culture. Can absorb team weight." },
  { name: "Action-Oriented", icon: Zap, body: "Moves work forward. Delivers. May sprint past alignment." },
  { name: "Transformational", icon: Sparkles, body: "Reinvents systems. Sponsors change. Can outpace the team's readiness." },
  { name: "Analytical", icon: Search, body: "Anchors decisions in data and rigor. May underweight the human factor." },
  { name: "Collaborative", icon: Users2, body: "Builds coalitions. Invites contribution. Guards against consensus drift." },
];

const speakingTopics = [
  "Impossible Is Where Your Genius Begins",
  "Make Great Decisions Faster",
  "Unbox Your Leadership",
  "The Genius Leader's Blueprint",
  "Unabashedly Me",
];

export default function Leadership() {
  return (
    <div data-testid="page-leadership">
      <section className="relative bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-16 md:pt-32 md:pb-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline text-[#0F4C5C]">Leadership Decision Intelligence</div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] mt-4">
              Leadership decisions <em className="italic text-[#0F4C5C]">shape</em> institutional outcomes.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#22223B]/80 leading-relaxed max-w-xl">
              The GLAD Score™ helps school leaders and educational teams understand how they make decisions, communicate, collaborate and lead change — before the next high-stakes moment.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/gla" data-testid="lead-cta-gla" className="btn-primary">
                Discover the Genius Leader Archetype™ &amp; GLAD Score™ <ArrowUpRight size={16} />
              </Link>
              <Link to="/contact?interest=workshop" data-testid="lead-cta-workshop" className="btn-secondary">
                Request a Workshop
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-lg overflow-hidden">
              <img src={FOUNDER_SPEAKING} alt="Dr. Crystal Gifford speaking on stage" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-8">
              <div className="overline">GLAD Score™</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                The six <em className="italic text-[#0F4C5C]">Genius Leadership Archetypes™.</em>
              </h2>
            </div>
            <p className="md:col-span-4 text-[#22223B]/75 leading-relaxed">
              Identify the natural leadership and decision-making patterns present in your leaders and teams.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {archetypes.map((a) => (
              <div key={a.name} className="card-soft p-8">
                <a.icon size={26} strokeWidth={1.4} className="text-[#0F4C5C]" />
                <h3 className="font-serif-display text-2xl mt-5">{a.name}</h3>
                <p className="mt-3 text-[15px] text-[#22223B]/75 leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/gla" data-testid="lead-see-full-gla" className="link-underline inline-flex items-center gap-1 text-sm">
              See the full Genius Leader Archetype™ &amp; GLAD Score™ showcase <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline">Applications</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              Where the GLAD Score works.
            </h2>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-[#22223B]/80">
            {[
              "Leadership retreats",
              "Professional development days",
              "Team development",
              "Strategic planning",
              "Administrative onboarding",
              "School culture initiatives",
              "Change management",
              "Leadership coaching",
              "Conference sessions",
              "Staff development",
            ].map((a) => (
              <div key={a} className="border-b border-[#0F4C5C]/10 py-3 flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-[#E3B23C]" /> {a}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#0F4C5C] text-[#FDFBF7] grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="aspect-square rounded-lg overflow-hidden bg-white/5">
              <img src={FOUNDER_SEATED} alt="Dr. Crystal Gifford" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="overline text-[#E3B23C]">Keynotes & Workshops</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              <em className="italic text-[#E3B23C]">Impossible</em> is where your genius begins.
            </h2>
            <p className="mt-6 text-[#FDFBF7]/80 leading-relaxed max-w-xl">
              Dr. Crystal Gifford helps leaders move past perceived limitations, recognize the intelligence already present in their teams, and make more courageous strategic decisions.
            </p>
            <ul className="mt-8 space-y-3">
              {speakingTopics.map((t) => (
                <li key={t} className="text-[17px] font-serif-display border-b border-white/10 pb-3">{t}</li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/speaking" className="btn-primary bg-[#E3B23C] border-[#E3B23C] text-[#22223B] hover:bg-[#d1a12f]">
                Explore Speaking <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
